<template>
  <div class="mk-dimensions">
    <div class="mk-dimensions__title">
      {{$t('dimension.title')}}
    </div>
    <span class="mk-dimensions__metric-label">
      {{$t('dimension.metricText')}}

      <toggle-button
        v-if="$i18n.locale !== 'de'"
        class="mk-dimensions__metric-switch"
        v-model="isImperial"
        :color="{checked: '#10699cb0', unchecked: '#10699cb0'}"
        :sync="true"
        :width="70"
        :labels="{
          checked: $t('dimension.metricSwitch.inch'),
          unchecked: $t('dimension.metricSwitch.meter'),
        }"
      />
      <span
        v-if="$i18n.locale === 'de'"
        class="mk-dimensions__metric-switch__text"
      >
        Millimeter
      </span>
    </span>

    <div class="mk-dimensions__container">
      <span class="mk-dimensions__container__title">
        {{$t('dimension.generalTitle')}}
      </span>
      <SizeInput
        :label="$t('dimension.height')"
        :value="sides[0].z"
        :focusCallback="()=>{$store.dispatch('setArrow', {type: ARROW_HEIGHT})}"
        :updateValue="(value)=>{$store.dispatch('setHeight', value)}"
        :isImperial="isImperial"
        :minValue="600"
        :maxValue="1600"
      />
      <SizeInput
        v-if="sides[0].raisedHeight"
        :label="$t('dimension.ledgeHeight')"
        :value="sides[0].raisedHeight"
        :focusCallback="()=>{$store.dispatch('setArrow', {type: ARROW_RAISED_HEIGHT})}"
        :updateValue="(value)=>{$store.dispatch('setRaisedHeight', value)}"
        :isImperial="isImperial"
        :minValue="20"
        :maxValue="1000"
      />
      <SizeInput
        v-if="sides[0].raisedWidth"
        :label="$t('dimension.ledgeDepth')"
        :value="sides[0].raisedWidth"
        :focusCallback="()=>{$store.dispatch('setArrow', {type: ARROW_RAISED_WIDTH})}"
        :updateValue="(value)=>{$store.dispatch('setRaisedWidth', value)}"
        :isImperial="isImperial"
        :minValue="150"
        :maxValue="sides[0].x * 500 < 420 ? sides[0].x * 500 : 420"
      />
    </div>

    <div
      v-for="(side, index) in sides"
      :key="`side-input-${index}`"
      class="mk-dimensions__container"
    >
      <span class="mk-dimensions__container__title">
        {{$t('dimension.counterSide')}} {{index + 1}}
      </span>
      <SizeInput
        :label="$t('dimension.length')"
        :value="sides[index].y"
        :focusCallback="()=>{$store.dispatch('setArrow', {type: ARROW_Y, side: index})}"
        :updateValue="(value)=>{$store.dispatch('setSide', {index, data: { y: value } })}"
        :isImperial="isImperial"
        :minValue="400"
        :maxValue="6000"
      />
      <SizeInput
        :label="$t('dimension.depth')"
        :value="sides[index].x"
        :focusCallback="()=>{$store.dispatch('setArrow', {type: ARROW_X, side: index})}"
        :updateValue="(value)=>{$store.dispatch('setSide', {index, data: { x: value }})}"
        :isImperial="isImperial"
        :minValue="300"
        :maxValue="1000"
      />
      <AngleInput
        v-if="index != sides.length - 1"
        :label="$t('dimension.angle')"
        :value="sides[index].childAngle"
        :focusCallback="()=>{}"
        :updateValue="(value)=>{$store.dispatch('setSide', {index, data: { childAngle: value }})}"
        :isImperial="isImperial"
      />
      <div
        v-if="index != sides.length - 1"
        class="mk-dimensions__round-corner"
      >
        {{$t('dimension.roundCorner')}} <toggle-button
          class="mk-dimensions__metric-switch"
          v-model="sides_round[index]"
          @change="(e) => toggleRoundCorner(e.value, index)"
          :color="{checked: '#9C0020', unchecked: '#e4e4e4'}"
          :sync="true"
        />
      </div>
      <SizeInput
        :label="$t('dimension.radius')"
        v-if="sides_round[index]"
        :value="sides[index].childRadius"
        :focusCallback="()=>{}"
        :updateValue="(value)=>{$store.dispatch('setSide', {index, data: { childRadius: value }})}"
        :isImperial="isImperial"
        :minValue="0"
        :maxValue="400"
      />
    </div>

  </div>
</template>

<script>
/**
 * Dimension Selection
 * @author David <david.prenninger@gmx.de>
 *
 * This component is part of the main configurator Sections.
 * It enables the user to change all relevant values of the counter configuration.
 *
 */
import SizeInput from '@/components/SizeInput.vue';
import AngleInput from '../components/AngleInput.vue';
import {
  ARROW_X,
  ARROW_Y,
  ARROW_HEIGHT,
  ARROW_RAISED_HEIGHT,
  ARROW_RAISED_WIDTH,
} from '../constants';
import { serializeDimensions, serializeOffset } from '../helpers/url';

export default {
  data() {
    return {
      ARROW_X,
      ARROW_Y,
      ARROW_HEIGHT,
      ARROW_RAISED_HEIGHT,
      ARROW_RAISED_WIDTH,
      sides_round: {},
    };
  },
  components: {
    SizeInput,
    AngleInput,
  },
  mounted() {
    serializeDimensions(this.sides, this);
    serializeOffset(this.offset, this);
  },
  computed: {
    offset() {
      return this.$store.getters.offset;
    },
    sides() {
      return this.$store.getters.dimensions;
    },
    isImperial: {
      get() {
        return !this.$store.getters.metric;
      },
      set(value) {
        this.$store.dispatch('setMetric', !value);
      },
    },
  },
  methods: {
    toggleRoundCorner(value, index) {
      console.log(value);
      if (!value) {
        this.$store.dispatch('setSide', { index, data: { childRadius: 0 } });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/defaults.scss";

.mk-dimensions {
  min-width: 300px;
  padding: 20px;
  height: 100%;
  overflow: auto;
  border-right: 1px solid $c_primary_light;
  box-sizing: border-box;

  @media (max-width: 900px) {
    border-right: none;
    border-top: 1px solid $c_primary_light;
  }

  &__title {
    font-size: 20px;
    text-align: left;
    font-weight: 500;
  }

  &__metric-label {
    color: $c_primary_lighter;
    background-color: transparent;
    border: none;
    display: flex;
    align-items: center;
    padding: 5px 0;
    font-size: 14px;
  }
  &__metric-switch {
    margin: 0 5px;

    &__text {
      margin-left: 5px;
    }
  }

  &__container {
    box-shadow: $bs_md;
    padding: 10px;
    margin: 10px 0;

    &__title {
      text-transform: uppercase;
      color: $c_primary_lighter;
    }
  }

  &__round-corner {
    font-size: 14px;
  }
}
</style>
